'use client'

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar, Clock, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Skeleton } from '@/components/ui/skeleton'

interface DeadlinesWidgetProps {
  deadlines: any[]
  loading?: boolean
}

export function DeadlinesWidget({ deadlines, loading }: DeadlinesWidgetProps) {
  const t = useTranslations()

  return (
    <Card className="border h-full flex flex-col">
      <CardHeader className="pb-3 md:pb-4">
        {loading ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-4 w-20 rounded-full" />
            </div>
            <Skeleton className="h-5 w-8 rounded-full" />
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <CardTitle className="text-base md:text-lg">{t('dashboard.deadlines')}</CardTitle>
              <span className="text-xs text-muted-foreground ms-2">{t('dashboard.nextDays')}</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {deadlines.length}
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-2 p-2">
                <div className="flex-1 min-w-0 space-y-2">
                  <Skeleton className="h-4 w-full rounded-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-3 w-16 rounded-full" />
                    <Skeleton className="h-3 w-12 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
            <Skeleton className="h-9 w-full rounded-lg mt-4" />
          </div>
        ) : deadlines.length > 0 ? (
          <>
            <div className="space-y-2 flex-1">
              {deadlines.map((deadline: any) => {
                const dueDate = new Date(deadline.due_date)
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                const isToday = daysUntil === 0
                const isTomorrow = daysUntil === 1

                let dueDateText = ''
                if (isToday) {
                  dueDateText = t('dashboard.today')
                } else if (isTomorrow) {
                  dueDateText = t('dashboard.tomorrow')
                } else {
                  dueDateText = t('dashboard.daysLeft', { days: daysUntil })
                }

                return (
                  <Link
                    key={deadline.id}
                    href={`/issues/${deadline.id}`}
                    className="block"
                  >
                    <div className="flex items-center gap-2 p-2 rounded-lg transition-colors group">
                      <div className="flex-1 min-w-0">
                        <div>
                          <span className="text-sm font-medium truncate group-hover:text-primary transition-colors block">
                            {deadline.subject}
                          </span>
                        </div>

                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          {deadline.priority?.name && (
                            <Badge className="text-[10px] px-1.5 py-0 flex-shrink-0 border-0 bg-transparent">
                              {deadline.priority.name}
                            </Badge>
                          )}

                          <div className={isToday ? 'text-red-600 dark:text-red-400 font-medium flex items-center gap-1' : 'flex items-center gap-1'}>
                            <Clock className="h-3 w-3" />
                            <span>{dueDateText}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>

            <Link href="/calendar" className="mt-4">
              <Button variant="outline" size="sm" className="w-full">
                <Calendar className="me-2 h-4 w-4" />
                {t('dashboard.viewCalendar')}
              </Button>
            </Link>
          </>
        ) : (
          <div className="text-center py-8 flex-1 flex flex-col items-center justify-center">
            <CheckCircle className="h-12 w-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-3 opacity-50" />
            <h3 className="font-medium text-sm mb-1">{t('dashboard.allClear')}</h3>
            <p className="text-xs text-muted-foreground">{t('dashboard.noDeadlines')}</p>
            <Link href="/calendar">
              <Button variant="outline" size="sm" className="mt-4">
                <Calendar className="me-2 h-4 w-4" />
                {t('dashboard.viewCalendar')}
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
